import { useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Upload, ImageIcon, Plus, Trash2 } from 'lucide-react';
import { settingsApi } from '../../../api/endpoints/settings';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { useToast } from '../../../components/ui/Alert';
import type { StoreSettings } from '../../../types/settings';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

function CharCounter({ value, max }: { value: string; max: number }) {
  const len = value.length;
  const ratio = len / max;
  const color = ratio > 1 ? 'text-danger' : ratio >= 0.8 ? 'text-warning' : 'text-success';
  return <p className={`text-xs text-end ${color}`}>{len}/{max}</p>;
}

interface Props { settings?: StoreSettings; storeId: string; }

/**
 * A domain-ownership meta tag, e.g.
 * <meta name="google-site-verification" content="abc123" />.
 * The storefront renders these into <head> on every public page.
 */
interface VerificationTag { name: string; content: string; }

interface SeoForm {
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  fbAppId: string;
  twitterHandle: string;
  verificationTags: VerificationTag[];
}

export function SeoTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, reset, watch, setValue, control, formState: { isDirty } } = useForm<SeoForm>({
    defaultValues: { metaTitle: '', metaDescription: '', keywords: '', ogTitle: '', ogDescription: '', ogImage: '', fbAppId: '', twitterHandle: '', verificationTags: [] },
  });

  const { fields: tagFields, append: appendTag, remove: removeTag } = useFieldArray({ control, name: 'verificationTags' });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast('error', t('settingsSeo.fileTooLarge', 'File size must be under 5MB'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast('error', t('settingsSeo.invalidFileType', 'Please select an image file'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setValue('ogImage', reader.result as string, { shouldDirty: true });
    };
    reader.readAsDataURL(file);
    // Reset file input so the same file can be re-selected
    e.target.value = '';
  };

  useEffect(() => {
    if (settings?.seo) {
      const s = settings.seo as Record<string, unknown> as Record<string, string | undefined>;
      // Saved either as [{name, content}] or as a plain {name: content} map.
      const rawTags = (settings.seo as Record<string, unknown>).verificationTags;
      const verificationTags: VerificationTag[] = Array.isArray(rawTags)
        ? (rawTags as VerificationTag[]).map((tg) => ({ name: tg?.name ?? '', content: tg?.content ?? '' }))
        : rawTags && typeof rawTags === 'object'
          ? Object.entries(rawTags as Record<string, string>).map(([name, content]) => ({ name, content }))
          : [];
      reset({
        metaTitle: s.metaTitle ?? s.title ?? '',
        metaDescription: s.metaDescription ?? s.description ?? '',
        keywords: s.keywords ?? '',
        ogTitle: s.ogTitle ?? '',
        ogDescription: s.ogDescription ?? '',
        ogImage: s.ogImage ?? '',
        fbAppId: s.fbAppId ?? '',
        twitterHandle: s.twitterHandle ?? '',
        verificationTags,
      });
    }
  }, [settings, reset]);

  const mutation = useMutation({
    mutationFn: (data: SeoForm) => {
      // Send both legacy (title/description) and new (metaTitle/metaDescription) field names
      // so data is written correctly regardless of which backend reads it
      const payload = {
        ...data,
        title: data.metaTitle,
        description: data.metaDescription,
        // Half-filled rows would render a meta tag with an empty content
        // attribute, which reads to the provider as a failed verification.
        verificationTags: data.verificationTags
          .map((tg) => ({ name: tg.name.trim(), content: tg.content.trim() }))
          .filter((tg) => tg.name && tg.content),
      };
      return settingsApi.updateSeo(storeId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  return (
    <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="mx-auto max-w-3xl space-y-6">
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">{t('settings.seo')}</h2>
        <div>
          <Input id="metaTitle" label={t('settings.metaTitle')} {...register('metaTitle')} />
          <CharCounter value={watch('metaTitle')} max={60} />
        </div>
        <div>
          <Textarea id="metaDescription" label={t('settings.metaDescription')} rows={3} {...register('metaDescription')} />
          <CharCounter value={watch('metaDescription')} max={160} />
        </div>
        <Input id="keywords" label={t('settings.keywords')} placeholder="keyword1, keyword2" {...register('keywords')} />
      </section>

      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">{t('settingsSeo.openGraph', 'Open Graph')}</h2>
        <p className="text-xs text-text-muted -mt-2">{t('settingsSeo.openGraphDesc', 'Controls how your store appears when shared on social media.')}</p>
        <Input id="ogTitle" label={t('settingsSeo.ogTitle', 'OG Title')} {...register('ogTitle')} />
        <Textarea id="ogDescription" label={t('settingsSeo.ogDescription', 'OG Description')} rows={2} {...register('ogDescription')} />
        <div>
          <label htmlFor="ogImage" className="text-sm text-text-secondary">{t('settingsSeo.ogImage', 'OG Image URL')}</label>
          <div className="mt-1.5 flex items-center gap-2">
            <Input id="ogImage" placeholder="https://..." {...register('ogImage')} className="flex-1" />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0"
            >
              <Upload className="h-3.5 w-3.5" />
              {t('settingsSeo.upload', 'Upload')}
            </Button>
          </div>
          {watch('ogImage') && (
            <div className="mt-2 rounded-lg border border-border-default overflow-hidden inline-block">
              <img
                src={watch('ogImage')}
                alt={t('settingsSeo.ogImagePreview', 'OG Image preview')}
                className="h-20 w-auto max-w-xs object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden h-20 w-32 flex items-center justify-center bg-bg-tertiary">
                <ImageIcon className="h-6 w-6 text-text-muted" />
              </div>
            </div>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input id="fbAppId" label={t('settingsSeo.fbAppId', 'Facebook App ID')} {...register('fbAppId')} />
          <Input id="twitterHandle" label={t('settingsSeo.twitterHandle', 'Twitter Handle')} placeholder="@yourstore" {...register('twitterHandle')} />
        </div>
      </section>
      {/* Domain verification meta tags */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-1">{t('settingsSeo.verification', 'Domain verification')}</h2>
        <p className="text-xs text-text-muted">{t('settingsSeo.verificationDesc', 'Meta tags added to every page of your storefront, for services that verify domain ownership (Google Search Console, Facebook, Pinterest, ad networks).')}</p>

        {tagFields.length === 0 && (
          <p className="text-xs text-text-muted">{t('settingsSeo.verificationEmpty', 'No verification tags yet.')}</p>
        )}

        {tagFields.map((field, i) => (
          <div key={field.id} className="flex items-end gap-2">
            <Input
              id={`verificationTags.${i}.name`}
              label={t('settingsSeo.verificationName', 'Tag name')}
              placeholder="google-site-verification"
              className="flex-1"
              {...register(`verificationTags.${i}.name` as const)}
            />
            <Input
              id={`verificationTags.${i}.content`}
              label={t('settingsSeo.verificationContent', 'Content')}
              placeholder="abc123..."
              className="flex-1"
              {...register(`verificationTags.${i}.content` as const)}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0 mb-0.5"
              onClick={() => removeTag(i)}
              aria-label={t('common.delete', 'Delete')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}

        <Button type="button" variant="secondary" size="sm" onClick={() => appendTag({ name: '', content: '' })}>
          <Plus className="h-3.5 w-3.5" />
          {t('settingsSeo.verificationAdd', 'Add tag')}
        </Button>
      </section>

      {/* Live Preview */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-5">
        <h2 className="text-base font-semibold mb-4">{t('settingsSeo.preview', 'Preview')}</h2>

        {/* Google Search Preview */}
        <div>
          <p className="text-xs text-text-muted mb-2">{t('settingsSeo.googlePreview', 'Google Search Result')}</p>
          <div className="rounded-lg border border-border-default bg-white p-4 text-start" style={{ fontFamily: 'Arial, sans-serif' }}>
            <p className="text-lg text-[#1a0dab] leading-snug truncate">{watch('metaTitle') || settings?.name || t('settingsSeo.yourStoreTitle', 'Your Store Title')}</p>
            <p className="text-sm text-[#006621] truncate mt-0.5">{settings?.customDomain || settings?.domain ? `https://${settings?.customDomain || settings?.domain}` : `https://${storeId}.00stores.com`}</p>
            <p className="text-sm text-[#545454] mt-1 line-clamp-2">{watch('metaDescription') || t('settingsSeo.yourDescription', 'Your store description will appear here...')}</p>
          </div>
        </div>

        {/* Social Share Preview */}
        <div>
          <p className="text-xs text-text-muted mb-2">{t('settingsSeo.socialPreview', 'Social Media Share')}</p>
          <div className="rounded-lg border border-border-default overflow-hidden max-w-md">
            {watch('ogImage') && (
              <div className="h-40 bg-bg-tertiary overflow-hidden">
                <img src={watch('ogImage')} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
            )}
            <div className="p-3 bg-bg-tertiary">
              <p className="text-[10px] text-text-muted uppercase tracking-wide">{settings?.customDomain || settings?.domain || `${storeId}.00stores.com`}</p>
              <p className="text-sm font-semibold text-text-primary mt-0.5 truncate">{watch('ogTitle') || watch('metaTitle') || settings?.name || 'Your Store'}</p>
              <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{watch('ogDescription') || watch('metaDescription') || ''}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <Button type="submit" isLoading={mutation.isPending} disabled={!isDirty}>{t('common.save')}</Button>
      </div>
    </form>
  );
}
